import React, { useEffect } from "react";
import EntityTable from "../../components/list/EntityList";
import {useAuth} from "../../utils/hooks/useAuth";
import useTableParams from "../../components/list/useTableParams";
import {useNavigate} from "react-router-dom";
import {useConfirm} from "material-ui-confirm";
import {useNotification} from "../../utils/hooks/useNotification";
import buildUrl from "../../utils/api/buildUrl";
import useSWR from "swr";
import {deleteFetcher, getFetcher} from "../../utils/api/fetchers";
import {PaginatedRaces, PublishStatus, Race, RaceStatus, ResultsStatus} from "../../schemas/races/racesSchema";
import {Season} from "../../schemas/seasons/seasonsSchema";
import UserRole from "../../schemas/user/roles";
import dayjs from "dayjs";
import {Button, Chip, IconButton} from "@mui/material";
import {racePublishStatusLabel, raceResultsStatusLabel, raceStatusLabel} from "../../utils/races/raceStatusLabels";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";

const Races: React.FC = () => {
    const {hasPermission} = useAuth();
    const {sortKeys, sortDirections, page, rowsPerPage, filters, setPage, setSort} = useTableParams();
    const navigate = useNavigate();
    const confirm = useConfirm();
    const {showNotification} = useNotification();

    const url = buildUrl('/races', {
        page,
        size: rowsPerPage,
        sortBy: sortKeys,
        sortDirections,
        filters
    });
    const {data, mutate} = useSWR<PaginatedRaces>(url, getFetcher, {keepPreviousData: true});
    const {data: seasons} = useSWR<Season[]>('/seasons/all', getFetcher);
    const editPermission = hasPermission(UserRole.COMMODORE);

    useEffect(() => {
        setSort("date", "desc");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreateRace = () => {
        navigate("/races/create");
    }

    const handleDetails = (raceId: number) => {
        navigate(`/races/${raceId}`);
    }

    const handleDelete = (raceId: number) => {
        confirm({
            description: `Opravdu chcete smazat tento závod?`,
            title: "Smazat závod",
            confirmationText: "Smazat",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        })
            .then(() => {
                    deleteFetcher(`/races/${raceId}`).then(() => {
                        showNotification('Závod byl smazán', 'success');
                        if (data?.content.length === 1 && data.page.number > 0) {
                            setPage(data.page.number - 1);
                        } else {
                            mutate();
                        }
                    });
                }
            ).catch(() => {
        });
    }

    return (
        <EntityTable
            title="Závody"
            controls={[
                <Button key="add" variant="contained" color="success" onClick={handleCreateRace}>
                    Vytvořit závod
                </Button>]}
            headers={[
                {key: "name", label: "Jméno", sortable: true, filtered: true},
                {
                    key: "date",
                    label: "Datum",
                    sortable: true,
                    filtered: true,
                    filterConfig: {
                        type: "date",
                    },
                    render: (date: string) => dayjs(date).format("DD.MM.YYYY")
                },
                {key: "location", label: "Místo", sortable: true, filtered: true},
                {
                    key: "raceSeason",
                    label: "Sezóna",
                    sortable: true,
                    filtered: true,
                    filterConfig: {
                        type: "select",
                        options: seasons?.map(season => ({
                            label: season.year.toString(),
                            value: season.year.toString()
                        })) || []
                    },
                    render: (season: Season) => season.year
                },
                {
                    key: "status",
                    label: "Stav",
                    sortable: true,
                    filtered: true,
                    filterConfig: {
                        type: "select",
                        options: [
                            {label: raceStatusLabel["PLANNED"], value: "PLANNED"},
                            {label: raceStatusLabel["IN_PROGRESS"], value: "IN_PROGRESS"},
                            {label: raceStatusLabel["FINISHED"], value: "FINISHED"},
                        ]
                    },
                    render: (status: RaceStatus) => (
                        <Chip
                            label={raceStatusLabel[status]}
                            color={status === "PLANNED" ? "primary" : status === "IN_PROGRESS" ? "error" : "success"}
                            sx={{minWidth: 100}}
                        />
                    )
                },
                {
                    key: "publishStatus",
                    label: "Umístění publikace",
                    sortable: true,
                    filtered: true,
                    filterConfig: {
                        type: "select",
                        options: [
                            {label: racePublishStatusLabel["PUBLISHED"], value: "PUBLISHED"},
                            {label: racePublishStatusLabel["UNPUBLISHED"], value: "UNPUBLISHED"},
                            {label: racePublishStatusLabel["ANNOUNCED"], value: "ANNOUNCED"},
                        ]
                    },
                    render: (publishStatus: PublishStatus) => (
                        <Chip
                            label={racePublishStatusLabel[publishStatus]}
                            color={publishStatus === "PUBLISHED" ? "success" : publishStatus === "ANNOUNCED" ? "primary" : "error"}
                            sx={{minWidth: 110}}
                        />
                    )
                },
                {
                    key: "resultStatus",
                    label: "Výsledky",
                    sortable: true,
                    filtered: true,
                    filterConfig: {
                        type: "select",
                        options: [
                            {label: raceResultsStatusLabel["PUBLISHED"], value: "PUBLISHED"},
                            {label: raceResultsStatusLabel["UNPUBLISHED"], value: "UNPUBLISHED"},
                        ]
                    },
                    render: (resultsStatus: ResultsStatus) => (
                        <Chip
                            label={raceResultsStatusLabel[resultsStatus]}
                            color={resultsStatus === "PUBLISHED" ? "success" : "error"}
                            sx={{minWidth: 110}}
                        />
                    )
                },
                {
                    key: "controls",
                    render: (race: Race) => (
                        <div>
                            {editPermission ? (
                                <>
                                    <IconButton color="info" title="Detaily a úprava"
                                                onClick={() => handleDetails(race.id)}>
                                        <EditIcon/>
                                    </IconButton>
                                    <IconButton color="error" title="Smazat"
                                                onClick={() => handleDelete(race.id)}>
                                        <DeleteIcon/>
                                    </IconButton>
                                </>
                            ) : (
                                <IconButton color="info" title="Detaily" onClick={() => handleDetails(race.id)}>
                                    <InfoIcon/>
                                </IconButton>
                            )}
                        </div>
                    )
                }
            ]}
            entities={data?.content || []}
            entitiesCount={data?.page.totalElements ?? 0}
            indexes
        />
    );
}

export default Races;